import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchUsers } from '@/store/users/actions';
import { selectUsers } from '@/store/users/selectors';

// `fetchUsers`' thunk condition only checks for an empty directory, so two
// callers warming at the same idle tick (the bell and a filter) would both
// fire. Share the in-flight dispatch instead.
let inFlight: unknown = null;

interface UseDeferredUserDirectoryOptions {
  skip?: boolean;
}

/**
 * Warms the account user directory into the Redux store, off the critical path.
 *
 * The notification bell is mounted in the app shell, so its `fetchUsers` on
 * mount ran on **every** full page load — including settings, tasks and reports,
 * none of which read the result. Sentry measured `GET /api/v1/users` at p75
 * 1,644ms on /tasks/jobs/scorecard, p75 1,607ms on /settings/api-management and
 * p75 5,835ms / p95 26,008ms on /settings/offices, competing with each page's
 * own requests while it loaded.
 *
 * It is deferred rather than removed because the prefetch is load-bearing
 * elsewhere: surfaces such as `candidate-job-drawer/ContactInfo` read
 * `selectUsers` without ever dispatching the fetch themselves, so dropping it
 * would leave them with an empty directory. The bell lives in the side nav,
 * which does not render on mobile — surfaces that need the directory there
 * should call this hook themselves.
 *
 * The timeout is a ceiling, not a delay — a permanently busy tab still fetches
 * rather than deferring forever. Older Safari falls back to a macrotask, which
 * still yields to paint.
 */
export const useDeferredUserDirectory = ({
  skip = false,
}: UseDeferredUserDirectoryOptions = {}) => {
  const dispatch = useDispatch();
  const users = useSelector(selectUsers);
  // Read as the initial condition rather than a dep: depending on `users` would
  // re-run the effect as the fetch resolves.
  const alreadyLoaded = Boolean(users?.length);

  useEffect(() => {
    if (skip || alreadyLoaded) return undefined;

    const run = () => {
      if (inFlight) return;
      inFlight = dispatch(fetchUsers(undefined));
      Promise.resolve(inFlight).finally(() => {
        inFlight = null;
      });
    };

    if (typeof window.requestIdleCallback === 'function') {
      const handle = window.requestIdleCallback(run, { timeout: 3000 });
      return () => window.cancelIdleCallback?.(handle);
    }

    const handle = window.setTimeout(run, 1);
    return () => window.clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once per enable; `alreadyLoaded` is read as the initial condition only
  }, [skip]);
};
